import { useState } from "react";
import { Sparkles, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { aiColumnsApi, type AiColumn } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import { useDealsListStore } from "@/store/deals-list-store";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import type { AiCellValue } from "./ai-column-cell";
import { isAiCellRunning } from "./ai-cell-state";

/**
 * Create and manage AI columns: a name and a prompt, answered per deal.
 *
 * Adding one deliberately does not fill it in. A workspace with two thousand
 * deals would be two thousand model calls on a single click, so existing rows
 * are filled on demand and only new deals answer themselves — which is what
 * `auto_run` controls, and why it is surfaced per column rather than buried.
 */
export function AiColumnsMenu({
  workspaceId,
  columns,
  onSaved,
  deals = [],
}: {
  workspaceId: string;
  columns: AiColumn[];
  onSaved: () => Promise<void> | void;
  /** Loaded deals, used to report how far a run has got. */
  deals?: DealTransformed[];
}) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [type, setType] = useState<AiColumn["type"]>("text");
  const [autoRun, setAutoRun] = useState(true);

  const sorted = [...columns].sort((a, b) => a.order - b.order);

  /**
   * How many loaded deals are still working on a column.
   *
   * A run is a queue on the server, not a request that finishes when the call
   * returns, so the cells themselves are the only honest source of progress.
   */
  const runningCount = (columnId: string) =>
    deals.filter((deal) =>
      isAiCellRunning(
        (deal.aiColumnValues as Record<string, AiCellValue> | undefined)?.[
          columnId
        ]
      )
    ).length;

  const save = async (next: Partial<AiColumn>[]) => {
    setIsSaving(true);
    try {
      await aiColumnsApi.save(workspaceId, next);
      await onSaved();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save the columns"));
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const add = async () => {
    if (!name.trim() || !prompt.trim()) return;
    try {
      await save([
        ...sorted,
        {
          name: name.trim(),
          prompt: prompt.trim(),
          type,
          order: sorted.length,
          auto_run: autoRun,
        },
      ]);
      toast.success(`Column "${name.trim()}" added`, {
        description: "New deals will fill it automatically. Use Run to fill existing ones.",
      });
      setName("");
      setPrompt("");
      setType("text");
      setAutoRun(true);
    } catch {
      // save() already surfaced the reason.
    }
  };

  const remove = async (column: AiColumn) => {
    try {
      await save(sorted.filter((c) => c._id !== column._id));
      toast.success(`Column "${column.name}" removed`);
    } catch {
      /* reported in save() */
    }
  };

  const runAll = async (column: AiColumn) => {
    // Each run is a model call per deal, so a second click is real money, not
    // just a duplicate request.
    if (startingId) return;
    setStartingId(column._id);

    // Light every cell up now. The request can take ten seconds to come back,
    // and a table of untouched cells for that long reads as a button that did
    // nothing. Each keeps whatever answer it already had while it works.
    const { updateDeal } = useDealsListStore.getState();
    const previousCells = new Map(
      deals.map((deal) => [deal.id, deal.aiColumnValues ?? {}])
    );
    deals.forEach((deal) => {
      const cells = (deal.aiColumnValues ?? {}) as Record<string, AiCellValue>;
      updateDeal(deal.id, {
        aiColumnValues: {
          ...cells,
          [column._id]: {
            ...(cells[column._id] ?? {}),
            status: "running",
            run_at: new Date().toISOString(),
            error: undefined,
          },
        },
      });
    });

    try {
      const result = await aiColumnsApi.run(workspaceId, column._id);
      toast.success(
        `Running "${column.name}" on ${result.queued} ${result.queued === 1 ? "deal" : "deals"}`,
        result.capped
          ? { description: "Capped at 200 per run — run again for the rest." }
          : undefined
      );
      await onSaved();
    } catch (error) {
      // Nothing was queued, so leaving every cell spinning would be a lie that
      // only a reload clears.
      previousCells.forEach((cells, dealId) =>
        updateDeal(dealId, { aiColumnValues: cells })
      );
      toast.error(getErrorMessage(error, "Couldn't start the run"));
    } finally {
      setStartingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Sparkles className="h-3.5 w-3.5" />
          AI columns
          {sorted.length > 0 && (
            <span className="text-muted-foreground">({sorted.length})</span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>AI columns</DialogTitle>
          <DialogDescription>
            Ask a question once and get an answer per deal.
          </DialogDescription>
        </DialogHeader>

        {sorted.length > 0 && (
          <ScrollArea className="max-h-[220px]">
            <div className="space-y-1 pr-3">
              {sorted.map((column) => {
                const running = runningCount(column._id);
                const isStarting = startingId === column._id;
                const isBusy = isStarting || running > 0;

                return (
                <div
                  key={column._id}
                  className="flex items-start gap-2 rounded-md border border-border p-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{column.name}</p>
                    {isBusy ? (
                      <p className="truncate text-xs text-primary">
                        {isStarting
                          ? "Starting…"
                          : `Working on ${running} of ${deals.length} ${
                              deals.length === 1 ? "deal" : "deals"
                            }`}
                      </p>
                    ) : (
                      <p className="truncate text-xs text-muted-foreground">
                        {column.prompt}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={() => runAll(column)}
                    // Held while the run is in flight and while cells are still
                    // working, so the queue is not stacked on top of itself.
                    disabled={isSaving || isBusy || !!startingId}
                  >
                    {isBusy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    {isBusy ? "Running" : "Run all"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground"
                    onClick={() => remove(column)}
                    disabled={isSaving}
                    aria-label={`Delete ${column.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <div className="space-y-2 border-t pt-3">
          <Input
            placeholder="Column name, e.g. Revenue"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Textarea
            rows={3}
            placeholder="What should it find? e.g. Find the company's most recent annual revenue and return it in USD."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Select value={type} onValueChange={(v) => setType(v as AiColumn["type"])}>
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="currency">Currency</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="boolean">Yes / No</SelectItem>
              </SelectContent>
            </Select>

            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={autoRun}
                onCheckedChange={(v) => setAutoRun(v === true)}
              />
              Fill automatically for new deals
            </label>

            <Button
              size="sm"
              className="ml-auto h-8 gap-1.5 text-xs"
              onClick={add}
              disabled={isSaving || !name.trim() || !prompt.trim()}
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Adding a column doesn&rsquo;t fill existing deals — that would be one
            model call per deal. Use <span className="font-medium">Run all</span>,
            or the sparkle in a cell.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
